<template>
  <div class="page-wrapper">
    <!-- Modern Header -->
    <header class="header">
      <div class="header-container">
        <router-link to="/" class="logo-link">
          <img src="@/assets/logo.png" alt="Nexthome4U Logo" class="logo" />
        </router-link>
      </div>
    </header>

    <!-- Main Content -->
    <main class="main-content">
      <div class="reset-card">
        <div class="card-header">
          <div class="icon-wrapper">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm10-10V7a4 4 0 0 0-8 0v4h8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h1 class="title">Set Your Password</h1>
          <p class="subtitle">Create a secure password for your account</p>
          <p class="email-display" v-if="email">{{ email }}</p>
        </div>

        <form class="form" @submit.prevent="submitForm">
          <div class="form-group">
            <label for="newPassword" class="label">New Password <span class="required">*</span></label>
            <div class="input-wrapper">
              <input 
                :type="showNewPassword ? 'text' : 'password'" 
                id="newPassword" 
                v-model="newPassword" 
                class="input"
                :class="{ 'input-error': errorMessage }"
                placeholder="Enter new password"
                required 
                autocomplete="new-password"
                :disabled="isSubmitting"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showNewPassword = !showNewPassword"
                tabindex="-1"
                :disabled="isSubmitting"
              >
                <svg v-if="!showNewPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <div class="form-group">
            <label for="confirmPassword" class="label">Confirm Password <span class="required">*</span></label>
            <div class="input-wrapper">
              <input 
                :type="showConfirmPassword ? 'text' : 'password'" 
                id="confirmPassword" 
                v-model="confirmPassword" 
                class="input"
                :class="{ 'input-error': errorMessage }"
                placeholder="Confirm new password"
                required 
                autocomplete="new-password"
                :disabled="isSubmitting"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showConfirmPassword = !showConfirmPassword"
                tabindex="-1"
                :disabled="isSubmitting"
              >
                <svg v-if="!showConfirmPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <div v-if="errorMessage" class="error-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            {{ errorMessage }}
          </div>

          <div class="button-group">
            <button type="submit" class="submit-button" :disabled="isSubmitting">
              <span v-if="!isSubmitting">Set Password</span>
              <div v-if="isSubmitting" class="loading-spinner">
                <div class="spinner"></div>
                <span>Setting Password...</span>
              </div>
            </button>
          </div>
        </form>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'TempPasswordVerify',
  data() {
    return {
      email: '',
      token: '',
      newPassword: '',
      confirmPassword: '',
      showNewPassword: false,
      showConfirmPassword: false,
      errorMessage: '',
      isSubmitting: false,
    };
  },
  mounted() {
    // Get email and token from URL query parameters
    this.email = this.$route.query.email || '';
    this.token = this.$route.query.token || '';

    // Validate that we have required parameters
    if (!this.email || !this.token) {
      this.errorMessage = 'Invalid verification link. Please check your email and try again.';
    }
  },
  methods: {
    async submitForm() {
      // Clear any previous error message
      this.errorMessage = '';

      // Validate that passwords match
      if (this.newPassword !== this.confirmPassword) {
        this.errorMessage = 'Passwords do not match';
        return;
      }

      // Validate password length
      if (this.newPassword.length < 6) {
        this.errorMessage = 'Password must be at least 6 characters long';
        return;
      }

      // Validate email and token
      if (!this.email || !this.token) {
        this.errorMessage = 'Invalid verification link. Please check your email and try again.';
        return;
      }

      // Set submitting state
      this.isSubmitting = true;

      try {
        // Get device token from localStorage
        const deviceToken = localStorage.getItem('DEVICE_REG_TOKEN');
        
        if (!deviceToken) {
          this.errorMessage = 'Device token not found. Please try again.';
          this.isSubmitting = false;
          return;
        }

        // API call to reset password with temp password token
        // Note: This endpoint uses additional params, so we'll use the smsApiWithDeviceToken directly
        const { smsApiWithDeviceToken } = await import('@/services/api');
        const response = await smsApiWithDeviceToken.get('/provider/v1/resetpassword', {
          params: {
            email: this.email,
            newpassword: this.newPassword,
            resettype: 'TEMP_PASSWORD',
            token: this.token,
          },
        });

        // Check API response
        if (response.data && response.data.success) {
          // Password set successfully, redirect to login
          this.$router.push({ 
            path: '/',
            query: { 
              message: 'Password set successfully. Please login with your new password.' 
            }
          });
        } else {
          this.errorMessage = response.data?.message || response.data?.reason || 'Failed to set password. Please try again.';
        }
      } catch (error) {
        console.error('Temp password verification error:', error);
        
        if (error.response) {
          // API returned an error response
          const errorData = error.response.data;
          const status = error.response.status;
          
          // For HTTP 400 errors, always use the reason field from API response
          if (status === 400) {
            this.errorMessage = errorData?.reason || errorData?.message || '';
            // If no reason/message provided for 400 error, don't show error (no generic messages)
          } else {
            // For other status codes, prioritize reason field
            this.errorMessage = errorData?.reason || errorData?.message || 'Failed to set password. Please check your link and try again.';
          }
        } else if (error.request) {
          // Request was made but no response received
          this.errorMessage = 'No response from server. Please check your internet connection and try again.';
        } else {
          // Error setting up the request
          this.errorMessage = 'An error occurred. Please try again.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>

* {
  box-sizing: border-box;
}

.page-wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  position: sticky;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 1rem 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.logo-link {
  display: inline-block;
  transition: transform 0.2s ease;
}

.logo-link:hover {
  transform: scale(1.05);
}

.logo {
  height: 40px;
  width: auto;
  max-width: 150px;
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  min-height: calc(100vh - 80px);
}

.reset-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 
    0 32px 64px rgba(91, 46, 142, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  padding: 3rem;
  width: 100%;
  max-width: 420px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 24px rgba(91, 46, 142, 0.25);
}

.icon-wrapper svg {
  width: 40px;
  height: 40px;
}

.title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

.subtitle {
  color: #6b7280;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 0.5rem;
}

.email-display {
  color: #5b2e8e;
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border-radius: 8px;
  display: inline-block;
}

.required {
  color: #ef4444;
  font-size: 0.875rem;
}

/* Form */
.form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input {
  width: 100%;
  padding: 0.875rem 1rem;
  padding-right: 3rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 400;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  font-family: inherit;
}

.input:focus {
  outline: none;
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.input:disabled {
  background: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}

.input-error {
  border-color: #ef4444;
}

.input-error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.password-toggle {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.password-toggle:hover:not(:disabled) {
  color: #5b2e8e;
}

.password-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 500;
}

.error-message svg {
  flex-shrink: 0;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.submit-button {
  width: 100%;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(91, 46, 142, 0.4);
}

.submit-button:active:not(:disabled) {
  transform: translateY(0);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 640px) {
  .reset-card {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }

  .title {
    font-size: 1.75rem;
  }

  .subtitle {
    font-size: 0.9375rem;
  }

  .icon-wrapper {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
  }

  .icon-wrapper svg {
    width: 32px;
    height: 32px;
  }

  .main-content {
    padding: 1.5rem 1rem;
  }
}

@media (max-width: 480px) {
  .reset-card {
    padding: 1.5rem;
  }

  .title {
    font-size: 1.5rem;
  }

  .header {
    padding: 0.75rem 0;
  }

  .logo {
    height: 32px;
  }
}
</style>

